import { Suspense } from "react";
import { MantineProvider } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { ModalsProvider } from "@mantine/modals";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, useRoutes, Navigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import {
  theme,
  CsrfInitializer,
  PrivateRoute,
  FullPageLoader,
  LoginPage,
  createAppMutationCache,
  createAppQueryCache,
} from "@livingsyncs/ui";
import { AuditorBoundary } from "./components/AuditorBoundary";
import { NoAccessPage } from "./pages/NoAccessPage";
import { buildRouteTree } from "./router/buildRouteTree";
import { auditPortal } from "./router/portals/audit.portal";

// Mantine styles
import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";

// Create React Query client — same policy as apps/portal (US-081/B-H1).
const queryClient = new QueryClient({
  mutationCache: createAppMutationCache(),
  queryCache: createAppQueryCache(),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const AuditorLayout = auditPortal.layout;

// Same shape as admin's own App.tsx (and ultimately frontend's own
// routes.tsx AppRoutes()) — a plain RouteObject[] via useRoutes(), not JSX
// <Routes>/<Route> — buildRouteTree already returns RouteObject[].
function AppRoutes() {
  return useRoutes([
    // Local sign-in (frontend split epic — no more gateway cross-origin
    // handoff, every app hosts its own /login using the shared LoginPage).
    { path: "/login", element: <LoginPage ownPortal="audit" /> },

    {
      path: "/no-access",
      element: (
        <PrivateRoute>
          <NoAccessPage />
        </PrivateRoute>
      ),
    },

    // audit.portal.ts's basePath ("/audit") kept as-is rather than
    // flattened to "/" now that this is its own origin — same deferred,
    // cosmetic-only decision as admin's "/admin" basePath.
    {
      path: auditPortal.basePath,
      element: (
        <PrivateRoute>
          <AuditorBoundary />
        </PrivateRoute>
      ),
      children: [
        {
          element: (
            <Suspense fallback={<FullPageLoader />}>
              <AuditorLayout />
            </Suspense>
          ),
          children: buildRouteTree(auditPortal.routes),
        },
      ],
    },

    // Anything else re-enters the app at its home route.
    { path: "*", element: <Navigate to={auditPortal.basePath} replace /> },
  ]);
}

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <h1>Something went wrong</h1>
          <p>We've been notified and will look into it.</p>
          <pre
            style={{
              textAlign: "left",
              background: "#f5f5f5",
              padding: "1rem",
            }}
          >
            {error instanceof Error
              ? error.message
              : "An unknown error occurred"}
          </pre>
          <button onClick={resetError}>Try again</button>
        </div>
      )}
    >
      <QueryClientProvider client={queryClient}>
        <MantineProvider theme={theme} defaultColorScheme="auto">
          <Notifications position="top-right" zIndex={1200} />
          <BrowserRouter>
            <CsrfInitializer />
            {/*
              ModalsProvider MUST be inside BrowserRouter — it (or its
              children) uses router context via useNavigate / useLocation.
              Hoisting it above the router would crash with "useNavigate()
              may be used only in the context of a <Router> component".

              Every "Create …" button on this app's pages opens its form via
              modals.open(), which dispatches a window CustomEvent whose only
              subscriber is this provider. Without it mounted the event is
              dropped silently — no throw, no console warning — and the
              buttons do nothing. It was missed when this app was scaffolded
              out of frontend (see frontend/src/App.tsx, which has it).
            */}
            <ModalsProvider
              /* a11y: give every modals.open() close (✕) button an accessible
                 name (Mantine's CloseButton has none by default). */
              modalProps={{
                closeButtonProps: { "aria-label": "Close dialog" },
              }}
            >
              <AppRoutes />
            </ModalsProvider>
          </BrowserRouter>
        </MantineProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
